/**
 * The New Project context — the hook and its types, without the dialog.
 *
 * Kept apart from NewProjectProvider (which renders NewProjectDialog and its
 * dependencies) so a light surface can read `useNewProject()` — e.g. the drive
 * pickers, which only need the tenant's display name — without importing the
 * dialog.
 */
import { createContext, useContext } from 'react'
import { request } from '@papan/api-client'

export interface OpenNewProjectOpts {
  /** 'miniapp' opens the mini-app variant (always shows the dialog, even on
   *  personal — the standalone/for-a-project choice applies regardless). */
  entityKind?: 'project' | 'miniapp'
  variant?: 'create' | 'promote'
  initialName?: string
  /** promote only */
  promoteFolderId?: string
  promoteNewName?: string
  /**
   * Personal-tenant quick surfaces (drag-drop, menu quick-create) that today
   * auto-create an "Untitled" with no prompt keep doing so (D7 "exactly as
   * today"). Surfaces that today prompt for a name (DriveView) leave this false,
   * so a personal tenant still gets the name field (audience section is hidden).
   */
  autoCreatePersonal?: boolean
  onDone: (folderId: string) => void
}

export interface NewProjectCtx {
  open: (opts: OpenNewProjectOpts) => void
  /**
   * Display name for the active tenant — the user's name on a personal tenant,
   * the org's name otherwise. Exposed here rather than threaded as a prop
   * because the provider is already GIVEN it and sits above every drive
   * surface: the alternative was passing the same string through four
   * SingleFileView call sites in two packages. `undefined` while the tenant
   * list is still loading, so consumers must keep a generic fallback.
   */
  tenantName?: string
}

export const NewProjectContext = createContext<NewProjectCtx | null>(null)

/**
 * Fallback used when a consumer renders OUTSIDE a NewProjectProvider (e.g. a
 * cached workspace mounted above the provider). Rather than hard-crash the app,
 * degrade to the safe non-dialog path: create-untitled / a plain promotion
 * rename, both of which the server default-locks (§5). Never throws.
 */
const FALLBACK: NewProjectCtx = {
  open: (o) => {
    if (o.variant === 'promote' && o.promoteFolderId && o.promoteNewName) {
      request(`/api/files/${o.promoteFolderId}`, { method: 'PUT', body: JSON.stringify({ name: o.promoteNewName }) })
        .then(() => o.onDone(o.promoteFolderId!)).catch(() => {})
      return
    }
    request<{ folderId: string }>(`/api/workspaces/create-untitled`, { method: 'POST', body: JSON.stringify({}) })
      .then((r) => o.onDone(r.folderId)).catch(() => {})
  },
}

export function useNewProject(): NewProjectCtx {
  return useContext(NewProjectContext) ?? FALLBACK
}
